import axios, { AxiosInstance } from 'axios'
import type { LogRequest, LogResponse } from '../types/api'

class LoggerService {
  private client: AxiosInstance
  private isEnabled: boolean = true

  constructor() {
    // Используем тот же базовый URL что и в apiClient
    const baseURL = (import.meta as any).env?.VITE_API_BASE_URL
    
    this.client = axios.create({
      baseURL,
      timeout: 5000, // Короткий таймаут для логов
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Отключаем логирование в development если нужно
    if (import.meta.env.DEV && import.meta.env.VITE_DISABLE_LOGGING === 'true') {
      this.isEnabled = false
    }
  }

  private getEnvironment(): string {
    if (import.meta.env.DEV) return 'dev'
    if (import.meta.env.PROD) return 'prod'
    return 'unknown'
  }

  private getCurrentPage(): string {
    return window.location.pathname + window.location.search
  }

  private getTelegramData() {
    const tg = window.Telegram?.WebApp
    return {
      init_data: tg?.initData || 'not_available',
      platform: tg?.platform || 'unknown',
      telegram_user_id: String(tg?.initDataUnsafe?.user?.id ?? 'not_available'),
    }
  }

  /**
   * Приводит все значения объекта к строкам.
   * Объекты и массивы сериализуются через JSON.stringify.
   */
  private stringifyValues(obj: Record<string, any>): Record<string, string> {
    const result: Record<string, string> = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined || value === null) continue
      result[key] = typeof value === 'object' ? JSON.stringify(value) : String(value)
    }
    return result
  }

  private async sendLog(logData: LogRequest): Promise<void> {
    if (!this.isEnabled) {
      console.log('📝 Logging disabled, would send:', logData)
      return
    }

    try {
      await this.client.post<LogResponse>('/store', logData)
    } catch (error) {
      // Молча игнорируем ошибки отправки логов
      // чтобы они не ломали основную функциональность
      console.warn('⚠️ Failed to send log (silently ignored):', error)
    }
  }

  async logError(
    error: Error | string | object,
    context?: {
      method?: string
      url?: string
      errorType?: string
      additionalData?: Record<string, any>
    }
  ): Promise<void> {
    const telegramData = this.getTelegramData()

    let messageObj: Record<string, any>
    if (typeof error === 'string') {
      messageObj = { error }
    } else if (error instanceof Error) {
      messageObj = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      }
    } else {
      messageObj = error as Record<string, any>
    }

    const logData: LogRequest = {
      project: 'secretchat',
      tags: this.stringifyValues({
        env: this.getEnvironment(),
        init_data: telegramData.init_data,
        user_agent: navigator.userAgent,
        platform: telegramData.platform,
        page: this.getCurrentPage(),
        method: context?.method,
        url: context?.url,
        error_type: context?.errorType || 'unknown',
        telegram_user_id: telegramData.telegram_user_id,
        ...context?.additionalData,
      }),
      message: this.stringifyValues(messageObj),
    }

    await this.sendLog(logData)
  }

  async logInfo(
    message: string | object,
    context?: {
      method?: string
      url?: string
      additionalData?: Record<string, any>
    }
  ): Promise<void> {
    const telegramData = this.getTelegramData()

    const messageObj: Record<string, any> =
      typeof message === 'string' ? { info: message } : (message as Record<string, any>)

    const logData: LogRequest = {
      project: 'secretchat',
      tags: this.stringifyValues({
        env: this.getEnvironment(),
        init_data: telegramData.init_data,
        user_agent: navigator.userAgent,
        platform: telegramData.platform,
        page: this.getCurrentPage(),
        method: context?.method,
        url: context?.url,
        telegram_user_id: telegramData.telegram_user_id,
        ...context?.additionalData,
      }),
      message: this.stringifyValues(messageObj),
    }

    await this.sendLog(logData)
  }

  // Метод для отключения логирования (например, для тестов)
  disable(): void {
    this.isEnabled = false
  }

  // Метод для включения логирования
  enable(): void {
    this.isEnabled = true
  }
}

// Создаем единственный экземпляр сервиса
export const logger = new LoggerService()
export default logger
